import { AnimatePresence, animate, motion, useMotionValue } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useTheme from "../admindashboard/useTheme.js";
import { Dock } from "./Dock.js";
import { Legend } from "./Legend.js";
import { MemberPanel } from "./MemberPanel.js";
import { OrgCanvas } from "./OrgCanvas.js";
import { TopBar } from "./TopBar.js";
import { CARD_SCALES } from "./data.js";
import { computeLayout, nodeBounds } from "./layout.js";
import { useOrg } from "./useOrg.js";

const MIN_ZOOM = 0.22;
const MAX_ZOOM = 2.4;

const DARK = {
  accent: "#d2f155",
  accentSoft: "rgba(210,241,85,0.65)",
  accentBg: "rgba(210,241,85,0.12)",
  accentBgHover: "rgba(210,241,85,0.15)",
  accentEdgeHover: "rgba(210,241,85,0.6)",
  accentSoftEdge: "rgba(210,241,85,0.5)",
  accentOnBg: "#141508",
  aura1: "#d2f155",
  pageBg: "#0a0b0e",
  containerBg: "#07080a",
  pageEdge: "rgba(255,255,255,0.08)",
  pageFontColor: "text-white",
};

const LIGHT = {
  accent: "#2563eb",
  accentSoft: "rgba(37,99,235,0.75)",
  accentBg: "rgba(37,99,235,0.10)",
  accentBgHover: "rgba(37,99,235,0.14)",
  accentEdgeHover: "rgba(37,99,235,0.65)",
  accentSoftEdge: "rgba(37,99,235,0.5)",
  accentOnBg: "#ffffff",
  aura1: "#60a5fa",
  pageBg: "#f7f9fc",
  containerBg: "rgba(255,255,255,0.55)",
  pageEdge: "rgba(15,23,42,0.10)",
  pageFontColor: "text-slate-900",
};

function OrgIntroCurtain({ show, isLight, C }) {
  const tileCls = isLight
    ? "relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white"
    : "relative grid size-20 place-items-center rounded-[28px] bg-white shadow-lifted p-2.5 border";
  const tileStyle = {
    borderColor: isLight ? "rgba(15,23,42,0.08)" : undefined,
    boxShadow: isLight
      ? "0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(15,23,42,0.22)"
      : undefined,
  };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: "blur(10px)" }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className={isLight
            ? "fixed inset-0 z-[120] grid place-items-center"
            : "routecore-app map-dots fixed inset-0 z-[120] grid place-items-center bg-paper"}
          style={
            isLight
              ? { background: "#ffffff", color: "#0f172a" }
              : undefined
          }
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: C.accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={isLight ? "mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900" : "mt-6 font-display text-3xl font-semibold tracking-tight"}
            >
              iSource Organization Chart
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className={isLight ? "mt-2 text-[10px] font-bold tracking-[0.34em] text-slate-500 uppercase" : "mt-2 text-[10px] font-bold tracking-[0.34em] text-ink-faint uppercase"}
            >
              Team Hierarchy System
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: isLight ? "rgba(15,23,42,0.12)" : undefined }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: C.accent }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.15, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={isLight ? "mt-3 font-display text-xs text-slate-500 italic" : "mt-3 font-display text-xs text-ink-faint italic"}
            >
              Mapping the org tree…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function OrgChartPage() {
  const org = useOrg();
  const { nodes, byId, rootIds, mode, depths } = org;
  const { isLight } = useTheme();
  const C = isLight ? LIGHT : DARK;

  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [highlightDept, setHighlightDept] = useState(null);
  const [edgeAnchor, setEdgeAnchor] = useState("center");
  const [focusToken, setFocusToken] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [dockExpanded, setDockExpanded] = useState(false);
  const toastSeq = useRef(0);

  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1250);
    return () => clearTimeout(t);
  }, []);

  const booting = !minElapsed;

  const panX = useMotionValue(typeof window !== "undefined" ? window.innerWidth / 2 : 600);
  const panY = useMotionValue(typeof window !== "undefined" ? window.innerHeight / 2 - 40 : 380);
  const zoom = useMotionValue(0.92);

  const cardScale = CARD_SCALES[org.cardSize];
  const sizeFactors = useMemo(() => {
    const m = new Map();
    nodes.forEach((n) => {
      if (n.scale !== undefined && n.scale !== 1) m.set(n.id, n.scale);
    });
    return m;
  }, [nodes]);
  const targets = useMemo(
    () => computeLayout(nodes, mode, cardScale, sizeFactors),
    [nodes, mode, cardScale, sizeFactors]
  );

  const fit = useCallback(
    (delay = 0, ids, opts) => {
      let source = targets;
      if (ids && ids.size) {
        const sub = new Map();
        targets.forEach((p, id) => {
          if (ids.has(id)) sub.set(id, p);
        });
        if (sub.size) source = sub;
      }
      const b = nodeBounds(source);
      const focused = source !== targets;
      const padX = focused ? 420 : 320;
      const padY = focused ? 300 : 240;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.max(b.w + padX, 520);
      const h = Math.max(b.h + padY, 380);
      const z = Math.min(Math.min(vw / w, vh / h), opts?.maxZoom ?? 1.15);
      const zClamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
      const cx = (b.minX + b.maxX) / 2;
      const cy = (b.minY + b.maxY) / 2;
      const t = opts?.soft
        ? { type: "spring", stiffness: 62, damping: 20, mass: 1.1, delay }
        : { type: "spring", stiffness: 90, damping: 19, delay };
      animate(panX, vw / 2 - cx * zClamped, t);
      animate(panY, vh / 2 - cy * zClamped, t);
      animate(zoom, zClamped, t);
    },
    [targets, panX, panY, zoom]
  );

  const highlightIds = useMemo(() => {
    if (!highlightDept) return null;
    const ids = new Set();
    nodes.forEach((n) => {
      if (n.department === highlightDept) ids.add(n.id);
    });
    return ids.size ? ids : null;
  }, [highlightDept, nodes]);

  const highlightIdsRef = useRef(null);
  highlightIdsRef.current = highlightIds;
  const fitRef = useRef(fit);
  fitRef.current = fit;

  const [focusing, setFocusing] = useState(false);
  const focusColor = useMemo(
    () => org.teams.find((t) => t.name === highlightDept)?.color ?? C.accent,
    [org.teams, highlightDept, C.accent]
  );

  useEffect(() => {
    if (!highlightDept || !highlightIds) {
      setFocusing(false);
      return;
    }
    setFocusing(true);
    const t = setTimeout(() => {
      setFocusing(false);
      fitRef.current(0, highlightIdsRef.current, { maxZoom: 1.05, soft: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [highlightDept, highlightIds]);

  const prevHighlight = useRef(null);
  useEffect(() => {
    if (prevHighlight.current && !highlightDept) {
      const t = setTimeout(() => fitRef.current(0, null, { soft: true }), 260);
      prevHighlight.current = null;
      return () => clearTimeout(t);
    }
    prevHighlight.current = highlightDept;
  }, [highlightDept]);

  const mountedRef = useRef(false);
  useEffect(() => {
    const t = setTimeout(
      () => fit(0, highlightIdsRef.current, highlightIdsRef.current ? { maxZoom: 1.05 } : undefined),
      mountedRef.current ? 420 : 1100
    );
    mountedRef.current = true;
    return () => clearTimeout(t);
  }, [mode, org.cardSize, fit]);

  const zoomStep = useCallback(
    (dir) => {
      const z0 = zoom.get();
      const z1 = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z0 * (dir > 0 ? 1.28 : 0.78)));
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const k = z1 / z0;
      const t = { type: "spring", stiffness: 160, damping: 22 };
      animate(zoom, z1, t);
      animate(panX, cx - (cx - panX.get()) * k, t);
      animate(panY, cy - (cy - panY.get()) * k, t);
    },
    [zoom, panX, panY]
  );

  const pushToast = useCallback((text) => {
    const id = ++toastSeq.current;
    setToasts((prev) => [...prev.slice(-2), { id, text }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2800);
  }, []);

  const handleAddChild = useCallback(
    (parentId) => {
      const created = org.addNode(parentId);
      if (!created) return;
      const parent = byId.get(parentId);
      setSelectedId(created.id);
      setFocusToken((v) => v + 1);
      pushToast(`Added under ${parent?.name ?? "member"} — edit details on the right`);
    },
    [org, byId, pushToast]
  );

  const handleRemove = useCallback(
    (id) => {
      const n = byId.get(id);
      const count = org.removeNode(id);
      setSelectedId(null);
      pushToast(
        count > 1
          ? `Removed ${n?.name ?? "member"} and ${count - 1} reports`
          : `Removed ${n?.name ?? "member"}`
      );
    },
    [org, byId, pushToast]
  );

  const handleReparent = useCallback(
    (id, newParent) => {
      org.reparent(id, newParent);
      pushToast(
        newParent
          ? `Now reporting to ${byId.get(newParent)?.name ?? "new manager"}`
          : `${byId.get(id)?.name ?? "Member"} promoted to the top level`
      );
      setTimeout(() => fitRef.current(0, highlightIdsRef.current, { soft: true }), 420);
    },
    [org, byId, pushToast]
  );

  const handleAddRoot = useCallback(() => {
    const created = org.addRootNode();
    setSelectedId(created.id);
    setFocusToken((v) => v + 1);
    pushToast("Added a card at the top level — edit details on the right");
    setTimeout(() => fitRef.current(0, null, { soft: true }), 450);
  }, [org, pushToast]);

  const handleReset = useCallback(() => {
    setSelectedId(null);
    setQuery("");
    setHighlightDept(null);
    pushToast("Organization centered");
    setTimeout(() => fitRef.current(0, null, { soft: true }), 120);
  }, [pushToast]);

  const teamCounts = useMemo(() => {
    const m = new Map();
    nodes.forEach((n) => m.set(n.department, (m.get(n.department) ?? 0) + 1));
    return m;
  }, [nodes]);

  const handleAddTeam = useCallback(
    (name, color) => {
      const ok = org.addTeam(name, color);
      if (ok) pushToast(`Team "${name.trim()}" created`);
      return ok;
    },
    [org, pushToast]
  );

  const handleRemoveTeam = useCallback(
    (name) => {
      const moved = org.removeTeam(name);
      if (highlightDept === name) setHighlightDept(null);
      pushToast(
        moved > 0
          ? `Removed "${name}" — ${moved} member${moved > 1 ? "s" : ""} reassigned`
          : `Removed team "${name}"`
      );
    },
    [org, pushToast, highlightDept]
  );

  const handleBackground = useCallback(
    (patch) => {
      org.setBackground((prev) => ({ ...prev, ...patch }));
    },
    [org]
  );

  const matchIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const set = new Set();
    nodes.forEach((n) => {
      if (`${n.name} ${n.role} ${n.department}`.toLowerCase().includes(q)) set.add(n.id);
    });
    return set;
  }, [query, nodes]);

  useEffect(() => {
    const resetScroll = () => {
      const el = document.getElementById("main-content-scroll");
      if (el) {
        el.scrollLeft = 0;
        try { window.scrollTo({ left: 0, top: window.scrollY, behavior: "auto" }); } catch (_) {}
      }
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
    };
    resetScroll();
    const t = window.setTimeout(resetScroll, 40);
    const t2 = window.setTimeout(resetScroll, 220);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [selectedId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedId(null);
        setHighlightDept(null);
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const selected = selectedId ? byId.get(selectedId) ?? null : null;
  const selectedDescendants = useMemo(
    () => (selectedId ? org.lineage(selectedId).descendants : new Set()),
    [org, selectedId]
  );

  return (
    <>
      <OrgIntroCurtain show={booting} isLight={isLight} C={C} />

      <motion.div
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: booting ? 0 : 1, scale: booting ? 0.985 : 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={
          isLight
            ? "nad-org relative h-full w-full min-h-[720px] select-none overflow-hidden rounded-2xl border bg-[#f7f9fc] font-['Inter'] text-slate-900 shadow-2xl antialiased"
            : "nad-org relative h-full w-full min-h-[720px] select-none overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0a0b0e] font-['Inter'] text-white shadow-2xl antialiased"
        }
        style={isLight ? { borderColor: C.pageEdge } : undefined}
      >
      {org.background.image && (
        <motion.div
          key={org.background.image.slice(-32)}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-none absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${org.background.image})`,
            filter: `blur(${org.background.blur}px)`,
            transform: org.background.blur > 0 ? "scale(1.05)" : undefined,
          }}
        />
      )}
      {org.background.image && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: isLight ? "#ffffff" : "#07080a",
            opacity: org.background.dim,
          }}
        />
      )}

      <div className="pointer-events-none absolute inset-0">
        <div
          className="aura-1 absolute -left-40 -top-40 h-[560px] w-[560px] rounded-full opacity-[0.07]"
          style={{ background: `radial-gradient(circle, ${C.aura1} 0%, transparent 65%)` }}
        />
        <div
          className="aura-2 absolute -bottom-52 -right-40 h-[640px] w-[640px] rounded-full opacity-[0.08]"
          style={{
            background: isLight
              ? "radial-gradient(circle, #c084fc 0%, transparent 65%)"
              : "radial-gradient(circle, #7c5cff 0%, transparent 65%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: isLight
              ? "radial-gradient(ellipse 90% 80% at 50% 45%, transparent 55%, rgba(15,23,42,0.08) 100%)"
              : "radial-gradient(ellipse 90% 80% at 50% 45%, transparent 55%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      <OrgCanvas
        nodes={nodes}
        targets={targets}
        depths={depths}
        byId={byId}
        rootIds={rootIds}
        radial={mode === "radial"}
        mode={mode}
        edgeAnchor={edgeAnchor}
        cardScale={cardScale}
        selectedId={selectedId}
        matchIds={matchIds}
        highlightDept={highlightDept}
        panX={panX}
        panY={panY}
        zoom={zoom}
        onSelect={setSelectedId}
        onAddChild={handleAddChild}
        lineage={org.lineage}
        isLight={isLight}
        C={C}
      />

      <TopBar
        members={nodes.length}
        levels={org.levels}
        departments={org.teams.length}
        query={query}
        matchCount={matchIds?.size ?? 0}
        onQuery={setQuery}
        isLight={isLight}
        C={C}
      />

      <AnimatePresence>
        {dockExpanded && (
          <motion.div
            key="legend-card"
            initial={{ y: 40, opacity: 0, scale: 0.96, filter: "blur(8px)" }}
            animate={{ y: 0, opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ y: 40, opacity: 0, scale: 0.96, filter: "blur(8px)" }}
            transition={{ type: "spring", stiffness: 180, damping: 24, mass: 0.78 }}
          >
            <Legend
              teams={org.teams}
              counts={teamCounts}
              highlight={highlightDept}
              onHighlight={setHighlightDept}
              onAdd={handleAddTeam}
              onRemove={handleRemoveTeam}
              isLight={isLight}
              C={C}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <Dock
        mode={mode}
        onMode={org.setMode}
        zoom={zoom}
        onZoomStep={zoomStep}
        onFit={() => fit()}
        onReset={handleReset}
        cardSize={org.cardSize}
        onCardSize={org.setCardSize}
        edgeAnchor={edgeAnchor}
        onEdgeAnchor={setEdgeAnchor}
        background={org.background}
        onBackground={handleBackground}
        onNotify={pushToast}
        rootCount={rootIds.size}
        onAddRoot={handleAddRoot}
        teams={org.teams}
        teamCounts={teamCounts}
        teamHighlight={highlightDept}
        onTeamHighlight={setHighlightDept}
        onTeamAdd={handleAddTeam}
        onTeamRemove={handleRemoveTeam}
        expanded={dockExpanded}
        onToggleExpanded={() => setDockExpanded((v) => !v)}
        isLight={isLight}
        C={C}
      />

      <AnimatePresence>
        {selected && (
          <MemberPanel
            node={selected}
            nodes={nodes}
            teams={org.teams}
            byId={byId}
            rootIds={rootIds}
            onAddRoot={handleAddRoot}
            focusToken={focusToken}
            descendantIds={selectedDescendants}
            onClose={() => setSelectedId(null)}
            onUpdate={org.updateNode}
            onAddChild={handleAddChild}
            onRemove={handleRemove}
            onReparent={handleReparent}
            onSelect={setSelectedId}
            isLight={isLight}
            C={C}
          />
        )}
      </AnimatePresence>

      <div className="pointer-events-none absolute bottom-6 right-6 z-30 hidden font-mono text-[9.5px] uppercase tracking-[0.22em] text-white/25 md:block">
        Drag to pan · Scroll to zoom · Click a card
      </div>

      <div className="pointer-events-none absolute left-1/2 top-6 z-50 flex -translate-x-1/2 flex-col items-center gap-2">
        <AnimatePresence>
          {focusing && highlightDept && (
            <motion.div
              key="focusing"
              initial={{ y: -18, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -12, opacity: 0, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className={
                isLight
                  ? "overflow-hidden rounded-full border bg-white shadow-xl backdrop-blur-xl"
                  : "overflow-hidden rounded-full border border-white/[0.1] bg-[rgba(16,17,21,0.9)] shadow-xl backdrop-blur-xl"
              }
              style={isLight ? { borderColor: "rgba(15,23,42,0.08)" } : undefined}
            >
              <div className="flex items-center gap-2 px-4 py-2">
                <span
                  className="h-1.5 w-1.5 animate-pulse rounded-full"
                  style={{
                    background: focusColor,
                    boxShadow: `0 0 8px ${focusColor}`,
                  }}
                />
                <span className={isLight ? "text-[12px] text-slate-700" : "text-[12px] text-white/80"}>
                  Isolating <span style={{ color: focusColor }}>{highlightDept}</span> — centering…
                </span>
              </div>
              <motion.span
                className="block h-[2px] origin-left"
                style={{ background: focusColor }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.5, ease: "linear" }}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ y: -18, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -12, opacity: 0, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className={
                isLight
                  ? "rounded-full border bg-white px-4 py-2 text-[12px] text-slate-700 shadow-xl backdrop-blur-xl"
                  : "rounded-full border border-white/[0.1] bg-[rgba(16,17,21,0.9)] px-4 py-2 text-[12px] text-white/80 shadow-xl backdrop-blur-xl"
              }
              style={isLight ? { borderColor: "rgba(15,23,42,0.08)" } : undefined}
            >
              <span
                className="mr-2 inline-block h-1.5 w-1.5 rounded-full align-middle"
                style={{ background: C.accent }}
              />
              {t.text}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
    </>
  );
}
